import { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
// @mui
import {
  Table,
  Button,
  TableRow,
  TableBody,
  TableCell,
  Container,
  TableContainer,
  TablePagination,
  Grid,
  Box,
  CardHeader,
  CardContent,
  Paper,
  Stack,
  Typography,
} from '@mui/material';
import { useDispatch, useSelector } from 'react-redux';
import Scrollbar from '../../../components/scrollbar';
// sections
import { UserListHead } from '../../../sections/@dashboard/user';

import { Page, TableSkeleton } from '../../../components';
import { fCurrency, fNumber } from '../../../utils/formatNumber';
import { fDateTime } from '../../../utils/formatTime';
import Iconify from '../../../components/iconify';
import { getMaintenanceRecord, getRepairDetail } from '../store/maintenanceActions';
import { getProfile } from '../../../shared/auth/authService';
import { EditRepair, NewRepair } from '.';

// ----------------------------------------------------------------------

const TABLE_HEAD = [
  { id: 'part_name', label: 'Part/Service', alignRight: false },
  { id: 'serial_number', label: 'Serial Number', alignRight: false },
  { id: 'quantity', label: 'Quantity', alignRight: false },
  { id: 'estimated_amount', label: 'Unit Cost', alignRight: false },
  { id: 'line_total', label: 'Line Total', alignRight: false },
  { id: 'expensesInclusive', label: 'Expenses Inclusive', alignRight: false },
  { id: 'warranty', label: 'Warranty', alignRight: false },
  { id: 'date', label: 'Date', alignRight: false },
  { id: '' },
];

RepairDetails.propTypes = {
  recordId: PropTypes.string,
};

export default function RepairDetails({ recordId }) {
  const dispatch = useDispatch();
  const { isLoading, maintenanceRepairs, itemsCount, maintenanceRecord } = useSelector(
    (state) => state.maintenanceReducer
  );

  // Read once per render rather than at module load, so a re-login as another
  // role is picked up without a hard refresh.
  const user = getProfile() || {};

  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [open, setOpen] = useState(false);
  const [openEdit, setOpenEdit] = useState(false);
  const [selectedRow, setSelectedRow] = useState({});

  // The record is already loaded into the store by MaintenanceTabs; this
  // component used to fetch it a second time with a raw axios call.
  const canEditRepairs =
    user?.role === 'SERVICE_PROVIDER' &&
    maintenanceRecord?.rsp_approval_status === 'ACCEPTED' &&
    maintenanceRecord?.admin_approval_status === 'APPROVED' &&
    !maintenanceRecord?.returned &&
    maintenanceRecord?.status !== 'RETURNED';

  const refresh = () => dispatch(getRepairDetail(recordId, { page: page + 1, rowsPerPage }));

  const handleClose = (saved) => {
    setOpen(false);
    if (saved) {
      refresh();
      dispatch(getMaintenanceRecord(recordId));
    }
  };

  const handleCloseEdit = (saved) => {
    setOpenEdit(false);
    if (saved) {
      refresh();
      dispatch(getMaintenanceRecord(recordId));
    }
  };

  const handleChangePage = (event, newPage) => setPage(newPage);

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  useEffect(() => {
    // Paging is now actually wired to the request; changing page used to do nothing.
    dispatch(getRepairDetail(recordId, { page: page + 1, rowsPerPage }));
  }, [dispatch, recordId, page, rowsPerPage]);

  return (
    <>
      <Page title="Repairs">
        {open && <NewRepair open={open} handleClose={handleClose} maintenanceId={recordId} />}
        {openEdit && <EditRepair open={openEdit} handleClose={handleCloseEdit} repairRecord={selectedRow} />}
        <Container maxWidth="xl">
          <Grid container spacing={3}>
            <Grid item xs={12} md={12} lg={12}>
              {canEditRepairs && (
                  <Box sx={{ p: 2 }}>
                    <Stack direction="row" sx={{ float: 'right' }} spacing={1}>
                      <Button
                        size="small"
                        variant="contained"
                        onClick={() => setOpen(true)}
                        startIcon={<Iconify icon="eva:plus-fill" />}
                      >
                        Add Repair Item
                      </Button>
                    </Stack>
                  </Box>
                )}
              <CardHeader title={'Parts'} subheader={'& Expenses'} />
              <CardContent>
                <Scrollbar>
                  <TableContainer sx={{ minWidth: 800 }} component={Paper}>
                    {isLoading ? (
                      <TableSkeleton />
                    ) : (
                      <Table size="small">
                        <UserListHead headLabel={TABLE_HEAD} rowCount={maintenanceRepairs?.length} />
                        <TableBody>
                          {maintenanceRepairs?.length > 0 ? (
                            maintenanceRepairs?.map((record) => (
                              <TableRow hover key={record?.id} tabIndex={-1} role="checkbox">
                                <TableCell align="left">{record?.part_name}</TableCell>
                                <TableCell align="left">
                                  {(record?.part_serial_number && record?.part_serial_number) || 'N/A'}
                                </TableCell>
                                <TableCell align="left">
                                  {(record?.item_type === 'PART' && fNumber(record?.quantity)) || 'N/A'}
                                </TableCell>
                                <TableCell align="left">{fCurrency(record?.estimated_cost)}</TableCell>
                                <TableCell align="left">{fCurrency(record?.line_total)}</TableCell>
                                <TableCell align="left">{(record?.includes_expenses && 'Yes') || 'No'}</TableCell>
                                <TableCell align="left">
                                  {(record?.warranty && record?.warranty) || 'N/A'} Years
                                </TableCell>
                                <TableCell align="left">{fDateTime(record?.date_created)}</TableCell>
                                {canEditRepairs && (
                                  <TableCell>
                                    <Stack spacing={2} direction="row">
                                      <Button
                                        size="small"
                                        variant="contained"
                                        onClick={() => {
                                          setSelectedRow(record);
                                          setOpenEdit(true);
                                        }}
                                      >
                                        Edit
                                      </Button>
                                    </Stack>
                                  </TableCell>
                                )}
                              </TableRow>
                            ))
                          ) : (
                            <TableRow>
                              <TableCell colSpan={TABLE_HEAD.length} align="center" sx={{ py: 5 }}>
                                <Typography variant="body2" color="text.secondary">
                                  No parts or services have been added to this job yet.
                                </Typography>
                              </TableCell>
                            </TableRow>
                          )}
                        </TableBody>
                      </Table>
                    )}
                  </TableContainer>
                </Scrollbar>

                <TablePagination
                  rowsPerPageOptions={[5, 10, 25, 50, 100]}
                  component="div"
                  count={itemsCount || 0}
                  rowsPerPage={rowsPerPage}
                  page={page}
                  onPageChange={handleChangePage}
                  onRowsPerPageChange={handleChangeRowsPerPage}
                />
              </CardContent>
            </Grid>
          </Grid>
        </Container>
      </Page>
    </>
  );
}
